import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from './util.service';
import { AppointmentBookingSource, IResponse } from '@signal/asp-data-commons';

export type CorporateDepartment = 'all' | 'service' | 'sales';
export type CorporateCategory = 'all' | 'service' | 'sales' | 'parts';
export type CorporateAppointmentType = 'ALL' | 'SERVICE' | 'SALES' | 'PARTS';
export type CorporateAppointmentRowCategory = 'Service' | 'Sales' | 'Parts';

export interface CorporateDashboardQuery {
  department: CorporateDepartment;
  category: CorporateCategory;
  type: string[];
  appointmentBookingSource?: string;
  vehicle?: string;
  dealer?: string;
  todayRecords: boolean;
  rosReceived: boolean;
  pageNo: number;
  size: number;
}

export interface CorporateKpiSummary {
  totalBooked: number;
  todaysAppointments: number;
  rosReceived: number;
  pendingTestDrive: number;
  pendingDelivery: number;
  yesterday: {
    appointments: number;
    ros: number;
    nsh: number;
  };
}

export interface CorporateBreakdownMetric {
  total: number;
  today: number;
  ros: number;
  types: string[];
}

export interface CorporateBreakdown {
  service: CorporateBreakdownMetric;
  sales: CorporateBreakdownMetric;
  parts: CorporateBreakdownMetric;
}

export interface FilterOption {
  value: string;
  display: string;
}

export const CORPORATE_TYPE_FILTERS: FilterOption[] = [
  // { value: 'maintenance', display: 'Maintenance' },
  // { value: 'complaint', display: 'Complaint' },
  // { value: 'ssc', display: 'SSC' },
  // { value: 'other', display: 'Other' },
  { value: 'Service', display: 'Service' },
  { value: 'delivery', display: 'Delivery' },
  { value: 'testDrive', display: 'Test Drive' },
  { value: 'inquiry', display: 'Inquiry' },
  { value: 'learning', display: 'Learning' },
  { value: 'pendingDelivery', display: 'Pending Delivery' },
  { value: 'pendingTestDrive', display: 'Pending Test Drive' }
];

export const CORPORATE_SOURCE_FILTERS: FilterOption[] = [
{ display: 'Customer', value: AppointmentBookingSource.ASP },
{ display: 'Dealer', value: AppointmentBookingSource.ASPBDC },
  { value: AppointmentBookingSource.DMS, display: 'DMS' },
  { value: 'Vehicle', display: 'Vehicle' },
  { value: 'OneApp', display: 'OneApp' },
  { value: 'Scheduler', display: 'Scheduler' },
  { value: AppointmentBookingSource.MSTC, display: 'MSTC' },
  { value: AppointmentBookingSource.FLAI, display: 'FLAI' },
  { value: AppointmentBookingSource.TCNA, display: 'TCNA' },
  // { value: 'T/L.com', display: 'T/L.com' },
  { value: 'CRM', display: 'CRM' }
];

export interface CorporateFiltersResponse {
  vehicles: string[];
  dealers: string[];
  types: FilterOption[];
  sources: FilterOption[];
}

export interface CorporateAppointmentRow {
  createdAt: string;
  scheduledAt: string;
  category: CorporateAppointmentRowCategory;
  type: string;
  customerName: string;
  vehicle: string;
  source: string;
  status: string;
  roNumber: string;
  dealer: string;
}

export interface CorporateAppointmentRecord {
  appointmentid: string;
  dealerCode: string;
  dealerName: string;
  appointmentStatus: string;
  appointmentCreatedDate: string;
  appointmentStartDate: string;
  vehicle: string;
  timeSlotId: string;
  repairOrderNumber: string;
  category: string;
  type: string;
  customer: string;
  appointmentBookingSource: string;
}

export interface CorporateAppointmentRecordsResponse {
  total: number;
  records: CorporateAppointmentRecord[];
}

export interface CorporateAppointmentsResponse {
  items: CorporateAppointmentRow[];
  totalCount: number;
}

export interface DashboardTotalAppointmentCount {
  total: number;
  sales: number;
  service: number;
  parts: number;
}

export interface DashboardTodayCount {
  total: number;
  sales: number;
  service: number;
  parts: number;
}

export interface DashboardYesterdayCount {
  totalAppointments: number;
  roCount: number;
}

export interface DashboardSalesCount {
  total: number;
  inquiry: number;
  testDrive: number;
  delivery: number;
  learning: number;
  pendingTestDrive: number;
  pendingDelivery: number;
}

export interface DashboardServiceCount {
  total: number;
  maintenance: number;
  complaint: number;
  ssc: number;
  other: number;
}

export interface DashboardCountsData {
  totalAppointmentCount: DashboardTotalAppointmentCount;
  todayCount: DashboardTodayCount;
  yesterdayCount: DashboardYesterdayCount;
  sales: DashboardSalesCount;
  service: DashboardServiceCount;
  parts: number;
  roCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class CorporateAppointmentDashboardService {
  private readonly aspAppointmentApi: string;

  constructor(private readonly http: HttpClient) {
    this.aspAppointmentApi = environment.aspAppointmentApi;
  }

  getDashboardCounts(): Observable<IResponse<DashboardCountsData>> {
    return this.http.get<IResponse<DashboardCountsData>>(`${this.aspAppointmentApi}appointment/dashboard/counts`).pipe(
      map((data: IResponse<DashboardCountsData>) => {
        return data;
      })
    );
  }

  getAppointmentRecords(query: CorporateDashboardQuery): Observable<CorporateAppointmentsResponse> {
    const body = {
      appointmentType: this.resolveAppointmentType(query),
      type: query.type || [],
      todayRecords: query.todayRecords,
      rosReceived: query.rosReceived,
      appointmentBookingSource: query.appointmentBookingSource || undefined,
      pageNo: query.pageNo || 1,
      size: query.size || 20
    };

    return this.http.post<IResponse<CorporateAppointmentRecordsResponse>>(`${this.aspAppointmentApi}appointment/records`, body).pipe(
      map((data: IResponse<CorporateAppointmentRecordsResponse>) => {
        return {
          totalCount: data?.data?.total || 0,
          items: (data?.data?.records || []).map(record => this.mapRecord(record))
        };
      })
    );
  }

  getBreakdown(_query: CorporateDashboardQuery): Observable<CorporateBreakdown> {
    return of({
      service: { total: 0, today: 0, ros: 0, types: [] },
      sales: { total: 0, today: 0, ros: 0, types: [] },
      parts: { total: 0, today: 0, ros: 0, types: [] }
    });
  }

  getFilters(_query: CorporateDashboardQuery): Observable<CorporateFiltersResponse> {
    return of({
      vehicles: [],
      dealers: [],
      types: CORPORATE_TYPE_FILTERS,
      sources: CORPORATE_SOURCE_FILTERS
    });
  }

  getAppointments(query: CorporateDashboardQuery): Observable<CorporateAppointmentsResponse> {
    return this.getAppointmentRecords(query);
  }

  private resolveAppointmentType(query: CorporateDashboardQuery): CorporateAppointmentType {
    if (query.department === 'service') {
      return 'SERVICE';
    }

    if (query.department === 'sales') {
      return 'SALES';
    }

    if (query.category === 'service') {
      return 'SERVICE';
    }

    if (query.category === 'parts') {
      return 'PARTS';
    }

    if (query.category === 'sales') {
      return 'SALES';
    }

    return 'ALL';
  }

  private mapRecord(record: CorporateAppointmentRecord): CorporateAppointmentRow {
    return {
      createdAt: record.appointmentCreatedDate || '',
      scheduledAt: [record.appointmentStartDate, record.timeSlotId].filter(Boolean).join(' '),
      category: this.toTitleCaseCategory(record.category),
      type: record.type || '',
      customerName: record.customer || '',
      vehicle: record.vehicle || '',
      source: record.appointmentBookingSource || '',
      status: record.appointmentStatus || '',
      roNumber: record.repairOrderNumber || '',
      dealer: [record.dealerName, record.dealerCode].filter(Boolean).join(' - ')
    };
  }

  private toTitleCaseCategory(value: string): 'Service' | 'Sales' | 'Parts' {
    const normalized = (value || '').toLowerCase();

    if (normalized === 'service') {
      return 'Service';
    }

    if (normalized === 'parts') {
      return 'Parts';
    }

    return 'Sales';
  }
}
